import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMyMenus } from "@/hooks/useMyMenus";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Check, Circle, Loader2, ClipboardCheck, History, AlertTriangle, MapPin } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import Documents from "@/components/app/Documents";
import ProjectComments from "@/components/app/ProjectComments";
import ProjectFinancialSummary from "@/components/app/ProjectFinancialSummary";
import ProjectProfitabilityCard from "@/components/app/ProjectProfitabilityCard";
import ProjectInvoicesCard from "@/components/app/ProjectInvoicesCard";

const CL_STATUS_COLOR = {
  draft: "bg-[#F3F4F6] text-[#4B5563]",
  submitted: "bg-[#DBEAFE] text-[#002FA7]",
  scheduled: "bg-[#DBEAFE] text-[#002FA7]",
  in_progress: "bg-[#FEF3C7] text-[#92400E]",
  partially_delivered: "bg-[#FEF3C7] text-[#B45309]",
  pending_items: "bg-[#FEE2E2] text-[#991B1B]",
  rescheduled: "bg-[#FED7AA] text-[#9A3412]",
  completed: "bg-[#D1FAE5] text-[#065F46]",
  cancelled: "bg-[#E5E7EB] text-[#4B5563]",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { menus } = useMyMenus(user?.role);
  const canViewProfitability = isAdmin || !!menus?.has("project-profitability");
  const [proj, setProj] = useState(null);
  const [tx, setTx] = useState(null);
  const [v2, setV2] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [audit, setAudit] = useState([]);
  const [confirmedProposal, setConfirmedProposal] = useState(null);
  const [lead, setLead] = useState(null);

  const load = () => {
    api.get(`/projects/${id}`).then((r) => {
      setProj(r.data);
      // Fetch the confirmed proposal for this project's lead (used to surface
      // a "Signed proposal" card in the sidebar).
      if (r.data?.lead_id) {
        api.get(`/proposals-v2?lead_id=${r.data.lead_id}`).then((pr) => {
          const conf = (pr.data || []).find((p) => p.status === "confirmed");
          setConfirmedProposal(conf || null);
        }).catch(() => setConfirmedProposal(null));
        api.get(`/leads/${r.data.lead_id}`).then((lr) => setLead(lr.data)).catch(() => setLead(null));
      } else {
        setConfirmedProposal(null);
        setLead(null);
      }
    });
    api.get(`/projects/${id}/transactions`).then((r) => setTx(r.data));
    api.get(`/delivery-checklists?project_id=${id}`).then((r) => setChecklists(r.data || []));
    api.get(`/projects/${id}/stages-v2`).then((r) => {
      setV2(r.data);
    });
    if (isAdmin) {
      api.get(`/projects/${id}/audit`).then((r) => setAudit(r.data || [])).catch(() => {});
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!proj || !v2) return <div className="p-4 sm:p-8 label-eyebrow">Loading…</div>;

  const toggleSub = async (stageKey, subKey, done) => {
    try {
      const r = await api.patch(`/projects/${id}/stages-v2`, { stage_key: stageKey, sub_key: subKey, done });
      setV2(r.data);
      toast.success(done ? "Activity completed" : "Activity reopened");
    } catch {
      toast.error("Failed to update activity");
    }
  };

  const toggleOwnershipChange = async (next) => {
    try {
      await api.patch(`/projects/${id}`, { ownership_change_needed: next });
      setProj((prev) => (prev ? { ...prev, ownership_change_needed: next } : prev));
      toast.success(next ? "Documentation on hold — ownership change flagged" : "Ownership change cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const toggleLoadEnhancement = async (next) => {
    try {
      await api.patch(`/projects/${id}`, { load_enhancement_needed: next });
      setProj((prev) => (prev ? { ...prev, load_enhancement_needed: next } : prev));
      toast.success(next ? "Load enhancement flagged" : "Load enhancement cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const toggleReadyPaymentCustomer = async (next) => {
    try {
      await api.patch(`/projects/${id}`, { ready_payment_customer: next });
      setProj((prev) => (prev ? { ...prev, ready_payment_customer: next } : prev));
      toast.success(next
        ? "Ready Payment Customer — Loan Processing stage skipped"
        : "Ready Payment Customer cleared — Loan Processing back in play");
      // Refresh stages so Loan Processing rows flip immediately.
      const r = await api.get(`/projects/${id}/stages-v2`);
      setV2(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const updateFeasibilityExpiry = async (dateStr) => {
    // Empty string = clear the field
    const payload = { feasibility_expiry_date: dateStr || null };
    try {
      await api.patch(`/projects/${id}`, payload);
      setProj((prev) => (prev ? { ...prev, feasibility_expiry_date: dateStr || null } : prev));
      toast.success(dateStr ? "Feasibility expiry updated" : "Feasibility expiry cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  return (
    <div data-testid="project-detail-page">
      <PageHeader
        eyebrow={`Project · ${proj.qr_code}`}
        title={proj.customer_name}
        subtitle={
          <span data-testid="project-contact-line" className="block space-y-0.5">
            <span className="block">{proj.capacity_kw} kW inverter{proj.panel_capacity_kw ? ` · ${proj.panel_capacity_kw} kW panel` : ""} · Contract {inr(proj.contract_value_inr)}</span>
            {proj.location && (
              <span className="flex items-center gap-1 text-[#4B5563]">
                <MapPin className="w-3.5 h-3.5" />
                {proj.map_link
                  ? <a href={proj.map_link} target="_blank" rel="noreferrer" className="hover:underline text-[#002FA7]" data-testid="project-address">{proj.location}</a>
                  : <span data-testid="project-address">{proj.location}</span>}
              </span>
            )}
            <span className="flex flex-wrap gap-x-4 gap-y-0.5 text-[#4B5563]">
              {lead?.phone && <span data-testid="project-phone">📞 <a href={`tel:${lead.phone}`} className="hover:underline">{lead.phone}</a></span>}
              {proj.consumer_number && <span data-testid="project-consumer-number">Consumer No. <b className="text-[#0a0a0a]">{proj.consumer_number}</b></span>}
              {proj.project_manager && <span data-testid="project-manager-name">PM: <b className="text-[#0a0a0a]">{proj.project_manager}</b></span>}
            </span>
          </span>
        }
        action={
          <div className="text-right">
            <div className="label-eyebrow">Completion</div>
            <div className="text-4xl font-black tracking-tighter text-[#002FA7]" data-testid="completion-pct">{v2.completion_pct}%</div>
            <div className="text-[10px] text-[#4B5563] mt-1">{v2.completed_activities} / {v2.total_activities} activities</div>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 sm:p-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flat-card">
            <div className="px-6 py-4 border-b border-[#E5E7EB] flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              <div>
                <div className="label-eyebrow">Project Tracker · {v2.stages.length} milestones</div>
                <div className="text-xs text-[#4B5563] mt-1">Current: <b className="text-[#0a0a0a]">{v2.current_stage_label}</b></div>
              </div>
              <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest font-bold">
                <Legend color="#10B981" label="Done" />
                <Legend color="#002FA7" label="In progress" />
                <Legend color="#9CA3AF" label="Pending" />
              </div>
            </div>

            <div className="px-6 py-3 border-b border-[#E5E7EB] bg-[#FAFAFA] flex items-center justify-between gap-3 flex-wrap" data-testid="ownership-change-row">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!proj.ownership_change_needed}
                  onChange={(e) => toggleOwnershipChange(e.target.checked)}
                  className="w-4 h-4 accent-[#DC2626] cursor-pointer"
                  data-testid="ownership-change-checkbox"
                />
                <span className="text-sm font-bold">Ownership Change Needed</span>
                <span className="text-[10px] text-[#4B5563]">— when ticked, documentation is put on hold and the project is flagged red in listings.</span>
              </label>
              {proj.ownership_change_needed && (
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#FEE2E2] text-[#991B1B]" data-testid="ownership-change-badge">
                  Documentation on Hold
                </span>
              )}
            </div>

            {proj.ownership_change_needed && (
              <div className="px-6 py-3 border-b border-[#E5E7EB] bg-[#FEF2F2] flex items-start gap-3" data-testid="ownership-change-banner">
                <AlertTriangle className="w-4 h-4 text-[#DC2626] shrink-0 mt-0.5" />
                <div className="text-xs text-[#991B1B]">
                  <b>Documentation section is on hold.</b> Ownership change is being processed for this project. Resume documentation activities only after the ownership transfer is complete — uncheck the box above to release the hold.
                </div>
              </div>
            )}

            {/* Load Enhancement toggle — same row visual language as ownership.
                When ticked, the project shows a solid red "Load Enhancement"
                badge in the Projects list so PMs can quickly filter follow-ups. */}
            <div className="px-6 py-3 border-b border-[#E5E7EB] bg-[#FAFAFA] flex items-center justify-between gap-3 flex-wrap" data-testid="load-enhancement-row">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!proj.load_enhancement_needed}
                  onChange={(e) => toggleLoadEnhancement(e.target.checked)}
                  className="w-4 h-4 accent-[#DC2626] cursor-pointer"
                  data-testid="load-enhancement-checkbox"
                />
                <span className="text-sm font-bold">Load Enhancement Needed</span>
                <span className="text-[10px] text-[#4B5563]">— flag projects that need KSEB load enhancement follow-up.</span>
              </label>
              {proj.load_enhancement_needed && (
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#FEE2E2] text-[#991B1B]" data-testid="load-enhancement-badge">
                  Load Enhancement Follow-up
                </span>
              )}
            </div>

            {/* Ready Payment Customer toggle — when ticked, Loan Processing
                (Stage 03) is skipped because the customer is paying up-front
                without a bank loan. All Stage 03 sub-activities are auto-marked
                done and its status forced to "completed". */}
            <div className="px-6 py-3 border-b border-[#E5E7EB] bg-[#FAFAFA] flex items-center justify-between gap-3 flex-wrap" data-testid="ready-payment-row">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!proj.ready_payment_customer}
                  onChange={(e) => toggleReadyPaymentCustomer(e.target.checked)}
                  className="w-4 h-4 accent-[#10B981] cursor-pointer"
                  data-testid="ready-payment-checkbox"
                />
                <span className="text-sm font-bold">Ready Payment Customer</span>
                <span className="text-[10px] text-[#4B5563]">— tick when the customer pays up-front (no loan). Loan Processing stage is skipped.</span>
              </label>
              {proj.ready_payment_customer && (
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#D1FAE5] text-[#065F46]" data-testid="ready-payment-badge">
                  Loan Processing Skipped
                </span>
              )}
            </div>

            {/* Feasibility Expiry Date — inline editable date so PMs can update
                it from the project page. Dashboard alert fires when ≤15 days
                to expiry (or already overdue). */}
            <div className="px-6 py-3 border-b border-[#E5E7EB] bg-[#FAFAFA] flex items-center gap-3 flex-wrap" data-testid="feasibility-expiry-row">
              <label className="flex items-center gap-2 select-none">
                <span className="text-sm font-bold">Feasibility Expiry Date</span>
              </label>
              <input
                type="date"
                value={proj.feasibility_expiry_date || ""}
                onChange={(e) => updateFeasibilityExpiry(e.target.value)}
                className="rounded-none border border-[#E5E7EB] h-8 px-2 text-xs"
                data-testid="feasibility-expiry-input"
              />
              {proj.feasibility_expiry_date && (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const exp = new Date(proj.feasibility_expiry_date);
                exp.setHours(0, 0, 0, 0);
                const days = Math.round((exp - today) / 86400000);
                if (days < 0) return <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#FEE2E2] text-[#991B1B]" data-testid="feasibility-overdue-badge">Overdue {Math.abs(days)}d</span>;
                if (days <= 15) return <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#FEF3C7] text-[#92400E]" data-testid="feasibility-soon-badge">{days}d left</span>;
                return <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[#D1FAE5] text-[#065F46]">{days}d left</span>;
              })()}
              <span className="text-[10px] text-[#4B5563]">— when set, projects expiring within 15 days show up on the dashboard alert.</span>
            </div>

            <div className="px-6 py-4 border-b border-[#E5E7EB]">
              <div className="h-2 w-full bg-[#F3F4F6]">
                <div className="h-full bg-[#002FA7] transition-all" style={{ width: `${v2.completion_pct}%` }} data-testid="progress-bar" />
              </div>
            </div>
            <MilestoneBranch stages={v2.stages} onSubToggle={toggleSub} projectId={id} />
          </div>

          <ProjectFinancialSummary projectId={id} />

          {canViewProfitability && <ProjectProfitabilityCard projectId={id} />}

          <ProjectComments projectId={id} />
        </div>

        <div className="space-y-4">
          <div className="flat-card p-6">
            <div className="label-eyebrow mb-3">QR / Sticker</div>
            <div className="bg-white border border-[#E5E7EB] p-4 flex flex-col items-center">
              <QRCodeSVG
                value={`${window.location.origin}/portal/${proj.qr_code}`}
                size={160}
                level="M"
                includeMargin={false}
                data-testid="project-qr-svg"
              />
              <div className="font-mono text-sm mt-3">{proj.qr_code}</div>
              <a
                href={`/portal/${proj.qr_code}`}
                target="_blank"
                rel="noreferrer"
                className="mt-3 text-xs font-bold text-[#002FA7] uppercase tracking-widest hover:underline"
                data-testid="open-portal-link"
              >
                Open Customer Portal →
              </a>
              <div className="text-[10px] text-[#4B5563] mt-2 text-center px-2 break-all">
                Scans to: {window.location.origin}/portal/{proj.qr_code}
              </div>
            </div>
          </div>

          <div className="flat-card p-6">
            <div className="label-eyebrow mb-3">System</div>
            <Row label="Panel" value={proj.panel_brand || "—"} />
            <Row label="Inverter SN" value={proj.inverter_serial || "—"} />
            <Row label="Warranty" value={`${proj.warranty_years} years`} />
            <Row label="PM" value={proj.project_manager || "—"} />
            {proj.map_link && (
              <div className="pt-3 mt-2 border-t border-[#F3F4F6]">
                <a
                  href={proj.map_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-xs font-bold text-[#002FA7] hover:underline uppercase tracking-widest"
                  data-testid="project-map-link"
                >
                  <MapPin className="w-3.5 h-3.5" /> Open in Google Maps ↗
                </a>
              </div>
            )}
          </div>

          {confirmedProposal && (
            <div className="flat-card p-6 border-[#002FA7]" data-testid="project-confirmed-proposal">
              <div className="label-eyebrow mb-3 text-[#002FA7]">Confirmed Proposal</div>
              <Row label="Version" value={`v${confirmedProposal.version}`} />
              <Row label="Option" value={`Option ${confirmedProposal.confirmed_option || 1}${(() => {
                const idx = confirmedProposal.confirmed_option || 1;
                const opt = idx === 2 ? confirmedProposal.option2 : confirmedProposal.option1;
                return opt?.capacity_kw ? ` · ${opt.capacity_kw} kW` : "";
              })()}`} />
              <Row label="Confirmed on" value={confirmedProposal.confirmed_at ? new Date(confirmedProposal.confirmed_at).toLocaleDateString() : "—"} />
              <Row label="Confirmed by" value={confirmedProposal.confirmed_by || "—"} />
              {confirmedProposal.totals?.grand_total_inr != null && (
                <Row label="Contract value" value={inr(confirmedProposal.totals.grand_total_inr)} />
              )}
              <div className="pt-3 mt-2 border-t border-[#F3F4F6]">
                <a
                  href={`/api/proposals-v2/${confirmedProposal.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-bold text-[#002FA7] hover:underline uppercase tracking-widest"
                  data-testid="project-confirmed-proposal-link"
                >
                  Open proposal PDF →
                </a>
              </div>
            </div>
          )}

          <ProjectInvoicesCard projectId={id} />

          <div className="flat-card p-6" data-testid="project-checklists-card">
            <div className="flex items-center justify-between mb-3">
              <div className="label-eyebrow flex items-center gap-2">
                <ClipboardCheck className="w-3.5 h-3.5" /> Delivery Checklists
              </div>
              <Link
                to="/delivery-checklists"
                className="text-[10px] font-bold text-[#002FA7] uppercase tracking-widest hover:underline"
                data-testid="checklists-all-link"
              >
                All →
              </Link>
            </div>
            {checklists.length === 0 ? (
              <div className="text-xs text-[#9CA3AF]">No checklists for this project yet.</div>
            ) : (
              <div className="space-y-2">
                {[...checklists]
                  .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")))
                  .map((c) => {
                    const items = c.items || [];
                    const total = items.length;
                    const fully = items.filter((i) => (i.item_status || (i.delivered ? "delivered" : "pending")) === "delivered").length;
                    const partial = items.filter((i) => (i.item_status) === "partially_delivered").length;
                    const statusCls = CL_STATUS_COLOR[c.status] || "bg-[#F3F4F6] text-[#4B5563]";
                    return (
                      <Link
                        key={c.id}
                        to={`/delivery-checklists?open=${c.id}`}
                        className="block border border-[#E5E7EB] hover:border-[#002FA7] hover:bg-[#F9FAFB] px-3 py-2 transition-colors"
                        data-testid={`project-checklist-${c.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-mono text-xs font-bold text-[#0a0a0a] truncate">
                              {c.checklist_no}
                              {c.parent_checklist_no && (
                                <span className="text-[9px] font-normal text-[#4B5563] ml-2">← rolled from {c.parent_checklist_no}</span>
                              )}
                            </div>
                            <div className="text-[10px] text-[#4B5563]">
                              {fully} / {total} delivered
                              {partial > 0 && <span className="text-[#B45309]"> · {partial} partial</span>}
                              {c.scheduled_delivery_date && <span> · sched {c.scheduled_delivery_date}</span>}
                              {(c.reschedule_history || []).length > 0 && <span className="text-[#F59E0B]"> · rescheduled {c.reschedule_history.length}x</span>}
                            </div>
                          </div>
                          <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 shrink-0 ${statusCls}`}>
                            {(c.status || "draft").replace(/_/g, " ")}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
              </div>
            )}
          </div>

          <div className="flat-card p-6">
            <Documents ownerType="project" ownerId={id} />
          </div>

          {isAdmin && (
            <div className="flat-card p-6" data-testid="project-audit-card">
              <div className="flex items-center justify-between mb-3">
                <div className="label-eyebrow flex items-center gap-2">
                  <History className="w-3.5 h-3.5" /> Audit Trail
                </div>
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#4B5563]">
                  {audit.length} event{audit.length === 1 ? "" : "s"}
                </span>
              </div>
              {audit.length === 0 ? (
                <div className="text-xs text-[#9CA3AF]">No edits recorded yet.</div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {[...audit].reverse().map((a) => (
                    <AuditEntry key={a.id} entry={a} />
                  ))}
                </div>
              )}
              <div className="text-[10px] text-[#4B5563] mt-3 italic">
                Admin-only. Captures every project edit, stage toggle, and material update.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * MilestoneBranch — vertical branching view of the 12-stage tracker.
 * Every stage is stacked top-to-bottom on a single trunk, with each stage's
 * sub-activities branching off as always-visible checkable rows to the right.
 * PMs no longer need to expand a stage to tick a sub-item — everything is
 * visible the moment the detail page loads.
 */
function MilestoneBranch({ stages, onSubToggle, projectId }) {
  return (
    <div className="p-4 sm:p-6" data-testid="milestone-branch">
      <div className="relative">
        {/* Trunk — the vertical line running through every stage node. */}
        <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-[#E5E7EB]" aria-hidden />
        <div className="space-y-4">
          {stages.map((s, i) => (
            <BranchNode
              key={s.key}
              stage={s}
              index={i}
              isLast={i === stages.length - 1}
              projectId={projectId}
              onSubToggle={(subKey, done) => onSubToggle(s.key, subKey, done)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function BranchNode({ stage, index, isLast, onSubToggle, projectId }) {
  const status = stage.status || "pending";
  const isDone = status === "completed";
  const isInProgress = status === "in_progress";
  const Icon = isDone ? Check : isInProgress ? Loader2 : Circle;
  const color = isDone ? "#10B981" : isInProgress ? "#002FA7" : "#9CA3AF";
  const bg = isDone ? "#D1FAE5" : isInProgress ? "#DBEAFE" : "#F3F4F6";
  const subs = stage.sub_activities || [];
  const subDone = subs.filter((sa) => sa.done).length;
  const pct = subs.length ? Math.round((subDone / subs.length) * 100) : 0;

  return (
    <div className="relative pl-12" data-testid={`milestone-${stage.key}`}>
      {/* Trunk node — icon badge sits on the trunk. */}
      <div
        className="absolute left-0 top-0 w-8 h-8 flex items-center justify-center border-2 border-white shrink-0 z-10"
        style={{ background: bg }}
      >
        <Icon
          className={`w-4 h-4 ${isInProgress ? "animate-spin" : ""}`}
          style={{ color }}
          strokeWidth={2.5}
        />
      </div>
      {/* Stage header row. */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-[#9CA3AF] font-bold">
            Stage {String(index + 1).padStart(2, "0")}
          </div>
          <div className="text-sm font-bold text-[#0a0a0a] flex items-center gap-2 flex-wrap">
            {stage.label}
            <span
              className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5"
              style={{ background: bg, color }}
            >
              {status.replace("_", " ")}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-24 bg-[#F3F4F6]">
            <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
          </div>
          <span className="text-[10px] font-mono text-[#4B5563] whitespace-nowrap">
            {subDone}/{subs.length}
          </span>
        </div>
      </div>
      {/* Sub-activity branches — each row shows a small horizontal connector
          from the trunk into the item so it reads as a proper tree branch. */}
      <div className="border-l-2 border-[#E5E7EB] ml-1 pl-4 space-y-1 pb-2">
        {subs.map((sa, si) => (
          sa.is_checklist ? (
            <Link
              key={sa.key}
              to={`/delivery-checklists?open=${sa.checklist_id}`}
              className={`relative flex items-start gap-2 py-1.5 px-2 border border-transparent hover:border-[#E5E7EB] hover:bg-[#FAFAFA] ${sa.done ? "bg-[#F0FDF4]" : ""}`}
              data-testid={`sub-${stage.key}-${sa.key}`}
            >
              <span className="absolute -left-4 top-4 w-3 h-0.5 bg-[#E5E7EB]" aria-hidden />
              <span
                className="w-4 h-4 mt-0.5 shrink-0 flex items-center justify-center border"
                style={{ borderColor: sa.done ? "#10B981" : "#9CA3AF", background: sa.done ? "#10B981" : "transparent" }}
                aria-hidden
              >
                {sa.done && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
              </span>
              <div className="min-w-0 flex-1">
                <div className={`text-xs leading-snug ${sa.done ? "line-through text-[#9CA3AF]" : "text-[#0a0a0a]"}`}>
                  <span className="text-[#9CA3AF] font-mono mr-1">{index + 1}.{si + 1}</span>
                  {sa.label}
                </div>
                {sa.done && sa.done_at && (
                  <div className="text-[9px] text-[#10B981] font-bold uppercase tracking-widest mt-0.5">
                    {fmtDate(sa.done_at)}{sa.done_by ? ` · ${sa.done_by}` : ""}
                  </div>
                )}
              </div>
              <span className="text-[9px] uppercase tracking-widest font-bold text-[#002FA7] whitespace-nowrap self-center">Open →</span>
            </Link>
          ) : (
            <label
              key={sa.key}
              className={`relative flex items-start gap-2 py-1.5 px-2 cursor-pointer border border-transparent hover:border-[#E5E7EB] hover:bg-[#FAFAFA] ${sa.done ? "bg-[#F0FDF4]" : ""}`}
              data-testid={`sub-${stage.key}-${sa.key}`}
            >
              {/* Branch elbow — horizontal tick that meets the vertical rail. */}
              <span
                className="absolute -left-4 top-4 w-3 h-0.5 bg-[#E5E7EB]"
                aria-hidden
              />
              <input
                type="checkbox"
                checked={!!sa.done}
                onChange={(e) => onSubToggle(sa.key, e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[#002FA7] cursor-pointer shrink-0"
                data-testid={`sub-checkbox-${stage.key}-${sa.key}`}
              />
              <div className="min-w-0 flex-1">
                <div className={`text-xs leading-snug ${sa.done ? "line-through text-[#9CA3AF]" : "text-[#0a0a0a]"}`}>
                  <span className="text-[#9CA3AF] font-mono mr-1">{index + 1}.{si + 1}</span>
                  {sa.label}
                </div>
                {sa.done && sa.done_at && (
                  <div className="text-[9px] text-[#10B981] font-bold uppercase tracking-widest mt-0.5">
                    {fmtDate(sa.done_at)}{sa.done_by ? ` · ${sa.done_by}` : ""}
                  </div>
                )}
              </div>
            </label>
          )
        ))}
        {subs.length === 0 && stage.key === "material_delivery" && (
          <div className="py-2 flex items-center gap-3 flex-wrap" data-testid="material-no-checklists">
            <span className="text-[10px] text-[#B45309] uppercase tracking-widest font-bold">
              No delivery checklists yet — this stage stays pending until one is created.
            </span>
            {projectId && (
              <Link
                to={`/delivery-checklists?new_for_project=${projectId}`}
                className="text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline"
                data-testid="material-create-checklist"
              >
                + Create Checklist
              </Link>
            )}
          </div>
        )}
        {subs.length === 0 && stage.key !== "material_delivery" && (
          <div className="py-2 text-[10px] text-[#9CA3AF] uppercase tracking-widest">
            No activities
          </div>
        )}
      </div>
      {isLast && null}
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-1.5 text-[#4B5563]">
      <div className="w-2 h-2" style={{ background: color }} />
      {label}
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 text-xs border-b border-[#F3F4F6] last:border-0">
      <span className="text-[#4B5563] uppercase tracking-wider">{label}</span>
      <span className="font-semibold text-[#0a0a0a]">{value}</span>
    </div>
  );
}
function FinRow({ label, value, color, bold }) {
  return (
    <div className="flex justify-between items-center py-1.5">
      <span className="text-xs text-[#4B5563] uppercase tracking-wider">{label}</span>
      <span className={`${bold ? "text-lg font-black" : "text-sm font-semibold"} tracking-tight`} style={{ color }}>{value}</span>
    </div>
  );
}

const EVENT_LABEL = {
  created: "Project Created",
  edited: "Project Edited",
  stage_v2_toggle: "Milestone Activity",
  legacy_stage_update: "Legacy Stage",
  material_update: "Material Updated",
};
const EVENT_COLOR = {
  created: "bg-[#D1FAE5] text-[#065F46]",
  edited: "bg-[#DBEAFE] text-[#1E40AF]",
  stage_v2_toggle: "bg-[#E0E7FF] text-[#3730A3]",
  legacy_stage_update: "bg-[#F3F4F6] text-[#4B5563]",
  material_update: "bg-[#FEF3C7] text-[#92400E]",
};

function AuditEntry({ entry }) {
  const ts = new Date(entry.at).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className="border-l-2 border-[#E5E7EB] pl-3 py-1" data-testid={`audit-entry-${entry.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${EVENT_COLOR[entry.event] || "bg-[#F3F4F6] text-[#4B5563]"}`}>
          {EVENT_LABEL[entry.event] || entry.event}
        </span>
        <span className="text-[10px] text-[#4B5563]">{ts}</span>
      </div>
      <div className="text-[11px] text-[#0a0a0a] mt-1">
        <b>{entry.by_name || "—"}</b>
        {entry.by_role && <span className="text-[#4B5563]"> · {entry.by_role}</span>}
      </div>
      {entry.changes && entry.changes.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {entry.changes.map((c, i) => (
            <li key={i} className="text-[11px]">
              <span className="font-semibold text-[#0a0a0a]">{c.field}</span>
              <span className="text-[#9CA3AF]"> {String(c.before ?? "—")} </span>
              <span className="text-[#002FA7]">→</span>
              <span className="text-[#0a0a0a]"> {String(c.after ?? "—")}</span>
            </li>
          ))}
        </ul>
      )}
      {entry.extra && Object.keys(entry.extra).length > 0 && (
        <div className="text-[10px] text-[#4B5563] mt-1 font-mono">
          {Object.entries(entry.extra)
            .filter(([, v]) => v !== null && v !== undefined && v !== "")
            .map(([k, v]) => `${k}=${v}`).join(" · ")}
        </div>
      )}
    </div>
  );
}
